import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../auth/services/auth.service';
import { logSessionEvent } from '../auth/session-log';

export const authGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
	const authService = inject(AuthService);
	const router = inject(Router);

	if (authService.isLoggedIn) {
		return of(true);
	}

	if (!authService.hasToken) {
		logSessionEvent({ reason: 'guard.redirect.no-token', at: new Date().toISOString() });
		return of(router.createUrlTree(['/onboarding']));
	}

	// Token present but expired: reuse the shared, deduped refresh call
	// (same one the 401 interceptor and the boot initializer use) instead of
	// redirecting immediately (see design D1/D2).
	return authService.refreshToken().pipe(
		map(() => true),
		catchError(() => {
			logSessionEvent({ reason: 'guard.redirect.refresh-failed', at: new Date().toISOString() });
			// Fire-and-forget (design D2): `logout()`'s POST has no timeout, so
			// awaiting it here could strand the user on a protected page. The
			// UrlTree below guarantees exit now; `clearLocalSession()`'s own
			// `navigateByUrl('/auth/login')` supersedes it a moment later.
			authService.logout().subscribe();
			return of(router.createUrlTree(['/onboarding']));
		})
	);
};
